import { Calendar, MapPin, Users, Wallet } from "lucide-react";

import { Badge, TournamentStatusBadge } from "@/ui";
import { formatEventDate, formatPrice } from "@/lib/format";
import { EVENT_COLOR, EVENT_COLOR_SOFT, EVENT_LABEL } from "@/lib/eventColors";
import type { Tournament, Training } from "@/types/api";

export type EventCardData =
  | { kind: "tournament"; data: Tournament }
  | { kind: "training"; data: Training };

interface Props {
  event: EventCardData;
  onClick?: () => void;
}

const LEVEL_LABEL: Record<string, string> = {
  all: "Для всех",
  beginner: "Начинающий",
  intermediate: "Средний",
  advanced: "Продвинутый",
};

export function EventCard({ event, onClick }: Props) {
  const isTournament = event.kind === "tournament";

  const title = isTournament ? event.data.name : event.data.title;
  const dateTime = event.data.date_time;
  const spotName = isTournament ? event.data.spot_name : event.data.spot_name ?? undefined;
  const price = isTournament ? event.data.entry_fee : event.data.price;
  const maxParticipants = event.data.max_participants;
  const registeredCount = isTournament
    ? event.data.registered_count
    : event.data.registered_count ?? undefined;

  return (
    <article
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        padding: 16,
        margin: "0 16px 12px",
        cursor: onClick ? "pointer" : "default",
        transition: "transform var(--d-fast) var(--ease-out)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {isTournament ? (
          <TournamentStatusBadge status={event.data.status} />
        ) : (
          <Badge kind="brand">{LEVEL_LABEL[event.data.level] ?? event.data.level}</Badge>
        )}
        <TypeBadge kind={isTournament ? "tournament" : "training"} />
      </div>

      <h3
        className="h3"
        style={{
          margin: 0,
          fontSize: 20,
          lineHeight: "26px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {title}
      </h3>

      <dl
        style={{
          margin: "12px 0 0",
          display: "grid",
          gap: 6,
          gridTemplateColumns: "1fr 1fr",
          fontFamily: "var(--font-body)",
          fontSize: 13,
          color: "var(--fg2)",
        }}
      >
        <Line icon={<Calendar size={14} />}>{formatEventDate(dateTime)}</Line>
        {spotName && <Line icon={<MapPin size={14} />}>{spotName}</Line>}
        <Line icon={<Users size={14} />}>
          {registeredCount != null
            ? `${registeredCount}/${maxParticipants}`
            : `до ${maxParticipants}`}
        </Line>
        <Line icon={<Wallet size={14} />}>{formatPrice(price)}</Line>
      </dl>
    </article>
  );
}

export function TypeBadge({ kind }: { kind: "tournament" | "training" | "challenge" }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-body)",
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 999,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        background: EVENT_COLOR_SOFT[kind],
        color: EVENT_COLOR[kind],
      }}
    >
      {EVENT_LABEL[kind]}
    </span>
  );
}

function Line({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span style={{ color: "var(--fg3)", display: "inline-flex" }}>{icon}</span>
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </span>
    </div>
  );
}
